import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type SprintReportWidget = Awaited<ReturnType<typeof trpc.sprints.listWidgets.query>>[number];
export type SprintReportWidgetConfig = SprintReportWidget["config"];
export type SprintReportWidgetData = Awaited<ReturnType<typeof trpc.sprints.widgetData.query>>[number];

// PM-defined Report widgets of a sprint board plus their values for the
// sprint the report is currently opened for.
export function useSprintWidgets(boardId: string | null, sprintId: string | null) {
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const widgetsQuery = useQuery(trpcClient.sprints.listWidgets.queryOptions(boardId ? { boardId } : skipToken));
  const dataQuery = useQuery(
    trpcClient.sprints.widgetData.queryOptions(boardId && sprintId ? { boardId, sprintId } : skipToken, {
      placeholderData: (previous) => previous,
    }),
  );
  const addMutation = useMutation(trpcClient.sprints.addWidget.mutationOptions());
  const updateMutation = useMutation(trpcClient.sprints.updateWidget.mutationOptions());
  const removeMutation = useMutation(trpcClient.sprints.removeWidget.mutationOptions());

  async function reload() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpcClient.sprints.listWidgets.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpcClient.sprints.widgetData.queryKey() }),
    ]);
  }

  async function addWidget(input: { config: SprintReportWidgetConfig; kind: SprintReportWidget["kind"]; title: string }) {
    if (!boardId) throw new Error("Sprint board is not selected");
    await addMutation.mutateAsync({ ...input, boardId });
    await reload();
  }

  async function updateWidget(input: { config: SprintReportWidgetConfig; title: string; widgetId: string }) {
    await updateMutation.mutateAsync(input);
    await reload();
  }

  async function removeWidget(widgetId: string) {
    await removeMutation.mutateAsync({ widgetId });
    await reload();
  }

  return {
    addWidget,
    data: dataQuery.data ?? [],
    isMutating: addMutation.isPending || removeMutation.isPending || updateMutation.isPending,
    removeWidget,
    updateWidget,
    widgets: widgetsQuery.data ?? [],
  };
}
